import { providers } from '../../providers';
import { baidu } from '../baidu';
import { kie } from '../kie';
import type { VideoModelEntry } from '../types';

// 价格表: 人民币/秒
const VIDU_Q3_TURBO = { '540p': 0.21875, '720p': 0.375, '1080p': 0.40625 };
const VIDU_Q3_PRO = { '540p': 0.28125, '720p': 0.625, '1080p': 0.75 };
const VIDU_Q3 = { '540p': 0.21875, '720p': 0.375, '1080p': 0.46875 };
// Kie 美元/秒：https://kie.ai/vidu-q4
const VIDU_Q4_USD = { '540p': 0.036, '720p': 0.076, '1080p': 0.096, '2k': 0.152, '4k': 0.312 };
// 查找价格函数
const lookup = (table: Record<string, number>, quality?: string) =>
    table[quality ?? ''] ?? Math.max(...Object.values(table));
// 美元汇率
const RMB_TO_USD = 1 / 6.8;
// rmb to usd 转换
const usd = (rmb: number) => rmb * RMB_TO_USD;

const COST_FACTOR = 1.5;

export const viduModels: VideoModelEntry[] = [
    {
        id: 'viduq4',
        chef: providers['vidu'],
        providers: [
            {
                ...providers.vidu,
                model: kie('viduq4'),
                getCost: ({ duration, quality }) => {
                    return duration * lookup(VIDU_Q4_USD, quality?.toLowerCase()) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality }) => {
                    return duration * lookup(VIDU_Q4_USD, quality?.toLowerCase()) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            first: {
                aspectRatios: [],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
                qualities: ['540p', '720p', '1080p', '2k', '4k'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '50MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
                qualities: ['540p', '720p', '1080p', '2k', '4k'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 15, maxSize: '50MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: true, maxCount: 3, maxSize: '50MB', minDuration: '3s', maxDuration: '12s', formats: ['audio/mpeg'] },
                },
            },
        },
        precheckByMode: {
            first: ['image'],
            ref: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['first', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'viduq3-turbo',
        chef: providers['vidu'],
        providers: [
            {
                ...providers.vidu,
                model: baidu('viduq3-turbo'),
                getCost: ({ duration, quality }) => {
                    return usd(duration * lookup(VIDU_Q3_TURBO, quality)) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality }) => {
                    return usd(duration * lookup(VIDU_Q3_TURBO, quality)) * COST_FACTOR * 100;
                }
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16', '4:3', '3:4', '1:1'],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
                qualities: ['540p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
                qualities: ['540p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['16:9', '9:16', '4:3', '3:4', '1:1'],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
                qualities: ['540p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['16:9', '9:16', '1:1'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
                qualities: ['540p', '720p', '1080p'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 7, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'viduq3-pro',
        chef: providers['vidu'],
        providers: [
            {
                ...providers.vidu,
                model: baidu('viduq3-pro'),
                getCost: ({ duration, quality }) => {
                    return usd(duration * lookup(VIDU_Q3_PRO, quality)) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality }) => {
                    return usd(duration * lookup(VIDU_Q3_PRO, quality)) * COST_FACTOR * 100;
                }
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16', '4:3', '3:4', '1:1'],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
                qualities: ['540p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
                qualities: ['540p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['16:9', '9:16', '4:3', '3:4', '1:1'],
                durations: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
                qualities: ['540p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'viduq3',
        chef: providers['vidu'],
        providers: [
            {
                ...providers.vidu,
                model: baidu('viduq3'),
                getCost: ({ duration, quality }) => {
                    return usd(duration * lookup(VIDU_Q3, quality)) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality }) => {
                    return usd(duration * lookup(VIDU_Q3, quality)) * COST_FACTOR * 100;
                }
            },
        ],
        configByMode: {
            ref: {
                aspectRatios: ['16:9', '9:16', '1:1'],
                durations: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
                qualities: ['540p', '720p', '1080p'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 7, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            ref: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
];